import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
/* import { ReactQueryDevtools } from "@tanstack/react-query-devtools"; */
import {
  PropsWithChildren,
  ReactNode,
  createContext,
  useContext,
  useEffect,
} from "react";
import {
  RouterProvider,
  createBrowserRouter,
  useLocation,
} from "react-router-dom";
import { Auth0Provider } from "@auth0/auth0-react";
import Auth0ProviderConfig from "../authConfig";
import LegislationReferenceProvider from "./LegislationReferenceProvider";
import ThemeProvider from "./theme/ThemeProvider";
import { FlagProvider } from "@unleash/proxy-client-react";
import { Toaster } from "@capacit-com/lara-law-shared-ui";
import TokenRefresher from "./RefreshToken";
import ResetStoresProvider from "./reset-stores/ResetStoresProvider";
import AuthenticatedProviders from "./AuthenticatedProviders";
import AppShellErrorBoundary from "@/components/state-handlers/AppShellErrorBoundary";
import ServicesProvider from "./services/ServicesProvider";
import ContentProvider from "./content/ContentProvider";
import { clearAppLocalStorage } from "@/lib/auth/clearAppStorage";
import {
  APP_VERSION_STORAGE_KEY,
  CURRENT_APP_VERSION,
} from "@/lib/appVersion";
import { shouldRetryQuery } from "@/lib/query/retry";
import { logMutationError, logQueryError } from "@/lib/query/logQueryErrors";

const unleashConfig = {
  url: import.meta.env.VITE_UNLEASH_URL,
  clientKey: import.meta.env.VITE_UNLEASH_TOKEN,
  refreshInterval: 15,
  appName: "Lara.law",
};

const queryClient = new QueryClient({
  // Every failed query and mutation reaches App Insights, whether or not its
  // hook logs it (LAR-520).
  queryCache: new QueryCache({ onError: logQueryError }),
  mutationCache: new MutationCache({ onError: logMutationError }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      // Refocusing a tab that had been hidden longer than staleTime refetched
      // every mounted query at once; each needed a token, and the resulting
      // stampede rotated the refresh token into a reuse breach that ended with
      // the user back on the dashboard (LAR-361). Live data already arrives over
      // SignalR and mutations invalidate explicitly, so the focus refetch bought
      // little and cost the session.
      refetchOnWindowFocus: false,
      // A 4xx is the API's settled answer, so retrying it three times just multiplies
      // the noise (LAR-456).
      retry: shouldRetryQuery,
    },
  },
});

const clearLocalStorageOnVersionChange = () => {
  const storedVersion = localStorage.getItem(APP_VERSION_STORAGE_KEY);
  if (storedVersion !== CURRENT_APP_VERSION) {
    // Preserve the Auth0 SDK cache/lock so a deploy busts app caches without
    // forcing a re-login (and without risking refresh-token reuse). See LAR-290.
    clearAppLocalStorage();
    localStorage.setItem(APP_VERSION_STORAGE_KEY, CURRENT_APP_VERSION);
    window.location.reload();
  }
};

// Children are forwarded through context so the data router is created
// exactly once. Re-creating the router on every render would reset the
// navigation history and drop in-flight blockers (e.g. dashboard unsaved
// changes).
const RouterChildrenContext = createContext<ReactNode>(null);

const RouterChildrenOutlet = () => {
  const children = useContext(RouterChildrenContext);
  const location = useLocation();
  return (
    <AppShellErrorBoundary resetKey={location.pathname}>
      <AuthenticatedProviders>{children}</AuthenticatedProviders>
    </AppShellErrorBoundary>
  );
};

const router = createBrowserRouter([
  {
    path: "*",
    element: <RouterChildrenOutlet />,
  },
]);

const AppProvider: React.FC<PropsWithChildren> = ({ children }) => {
  useEffect(() => {
    clearLocalStorageOnVersionChange();
  }, []);

  return (
    <FlagProvider config={unleashConfig}>
      <Auth0Provider {...Auth0ProviderConfig}>
        <TokenRefresher />
        <QueryClientProvider client={queryClient}>
          <ResetStoresProvider>
            <ServicesProvider>
              <ContentProvider>
                <ThemeProvider>
                  <LegislationReferenceProvider>
                    <RouterChildrenContext.Provider value={children}>
                      <RouterProvider router={router} />
                    </RouterChildrenContext.Provider>
                    <Toaster richColors />
                  </LegislationReferenceProvider>
                </ThemeProvider>
              </ContentProvider>
            </ServicesProvider>
            {/* <ReactQueryDevtools
                initialIsOpen={false}
                buttonPosition="bottom-right"
              /> */}
          </ResetStoresProvider>
        </QueryClientProvider>
      </Auth0Provider>
    </FlagProvider>
  );
};

export default AppProvider;
